import React, { useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { IconButton, useTheme, Box, Typography } from "@mui/material"
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
import { toast } from "react-toastify"
import { appActions } from "redux/app/slice/app"
import moment from "moment"
import {
  formatDate,
  formatMonthYear,
  isFutureDate,
  isInHoverRange,
  isInRange
} from "helpers/functions"
import styles from "../groupAnalytics.module.scss"

const AnalyticsDateRangeFilter = ({ isDMV = false }) => {
  const dispatch = useDispatch()
  const currentFilters = useSelector((store) => store.app?.filters || {})
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [startDate2, setStartDate2] = useState("")
  const [endDate2, setEndDate2] = useState("")
  const [isCompare, setIsCompare] = useState(false)
  const [activeRange, setActiveRange] = useState("range1")
  const [showCalendar, setShowCalendar] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [hoverDate, setHoverDate] = useState(null)
  const [selectionMode, setSelectionMode] = useState("start")
  const containerRef = useRef(null)
  const theme = useTheme()

  useEffect(() => {
    if (currentFilters.startDate) setStartDate(currentFilters.startDate)
    if (currentFilters.endDate) setEndDate(currentFilters.endDate)
    if (currentFilters.startDate2) setStartDate2(currentFilters.startDate2)
    if (currentFilters.endDate2) setEndDate2(currentFilters.endDate2)
    if (currentFilters.isCompare !== undefined) setIsCompare(currentFilters.isCompare)
  }, [currentFilters])

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const firstDayOfMonth = new Date(year, month, 1).getDay()

    const prevMonthDays = []
    if (firstDayOfMonth > 0) {
      const prevMonth = month === 0 ? 11 : month - 1
      const prevMonthYear = month === 0 ? year - 1 : year
      const daysInPrevMonth = new Date(prevMonthYear, prevMonth + 1, 0).getDate()

      for (let i = 0; i < firstDayOfMonth; i++) {
        const day = daysInPrevMonth - firstDayOfMonth + i + 1
        const date = new Date(prevMonthYear, prevMonth, day)
        prevMonthDays.push({
          date: formatDate(date),
          day,
          isCurrentMonth: false
        })
      }
    }

    const currentMonthDays = []
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      currentMonthDays.push({
        date: formatDate(date),
        day,
        isCurrentMonth: true
      })
    }

    const nextMonthDays = []
    const totalDaysSoFar = prevMonthDays.length + currentMonthDays.length
    if (totalDaysSoFar < 42) {
      const nextMonth = month === 11 ? 0 : month + 1
      const nextMonthYear = month === 11 ? year + 1 : year

      for (let day = 1; nextMonthDays.length + totalDaysSoFar < 42; day++) {
        const date = new Date(nextMonthYear, nextMonth, day)
        nextMonthDays.push({
          date: formatDate(date),
          day,
          isCurrentMonth: false
        })
      }
    }

    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays]
  }

  const handleDateClick = (dateStr) => {
    const clickedDate = new Date(dateStr)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    clickedDate.setHours(0, 0, 0, 0)

    if (clickedDate > today) return

    const currentStart = activeRange === "range1" ? startDate : startDate2
    const setStart = activeRange === "range1" ? setStartDate : setStartDate2
    const setEnd = activeRange === "range1" ? setEndDate : setEndDate2

    if (selectionMode === "start") {
      setStart(dateStr)
      setEnd("")
      setSelectionMode("end")
    } else {
      const start = new Date(currentStart)
      start.setHours(0, 0, 0, 0)

      if (clickedDate < start) {
        setStart(dateStr)
        setEnd("")
      } else {
        setEnd(dateStr)
        setSelectionMode("start")
      }
    }
  }

  const handleApply = () => {
    if (isCompare) {
      if (startDate === startDate2 && endDate === endDate2) {
        toast.error("Comparison range cannot be the same as primary range", {
          position: "bottom-left"
        })
        return
      }
    }

    const payload = {
      ...currentFilters,
      startDate,
      endDate,
      startDate2,
      endDate2,
      isCompare
    }

    dispatch(appActions.setFilters(payload))
    setShowCalendar(false)
  }

  const handleToday = () => {
    const today = formatDate(new Date())
    const setStart = activeRange === "range1" ? setStartDate : setStartDate2
    const setEnd = activeRange === "range1" ? setEndDate : setEndDate2

    // Check if today is valid for active range
    const todayDate = new Date()
    todayDate.setHours(0, 0, 0, 0)

    setStart(today)
    setEnd("")
    setSelectionMode("start")

    const payload = {
      ...currentFilters,
      date: today,
      isCompare
    }

    if (activeRange === "range1") {
      payload.startDate = today
      payload.endDate = ""
      payload.startDate2 = startDate2
      payload.endDate2 = endDate2
    } else {
      payload.startDate = startDate
      payload.endDate = endDate
      payload.startDate2 = today
      payload.endDate2 = ""
    }

    dispatch(appActions.setFilters(payload))
    setShowCalendar(false)
  }

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const nextMonth = () => {
    const today = new Date()
    const nextMonthDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    if (nextMonthDate > today) return
    setCurrentMonth(nextMonthDate)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowCalendar(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const displayDate = (date) => (date ? moment(date).format("DD/MM/YYYY") : "dd/mm/yyyy")

  const weekDays = ["S", "M", "T", "W", "T", "F", "S"]

  // Determine which range's start/end dates to use for calendar highlighting
  const activeStart = activeRange === "range1" ? startDate : startDate2
  const activeEnd = activeRange === "range1" ? endDate : endDate2

  const isDateDisabled = (dateStr) => {
    const date = new Date(dateStr)
    date.setHours(0, 0, 0, 0)

    return false
  }

  const handleCompareChange = (e) => {
    const newValue = e.target.checked
    setIsCompare(newValue)
    if (!newValue) {
      setActiveRange("range1")
    }
    dispatch(
      appActions.setFilters({
        ...currentFilters,
        isCompare: newValue,
        startDate2,
        endDate2
      })
    )
  }

  return (
    <Box ref={containerRef} className={styles.wrapper}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {isDMV && (
          <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
            <input
              type="checkbox"
              id="compareMode"
              checked={isCompare}
              onChange={handleCompareChange}
              style={{ cursor: "pointer" }}
            />
            <label
              htmlFor="compareMode"
              style={{ color: theme.palette.text.primary, fontSize: "14px", cursor: "pointer" }}>
              Compare
            </label>
          </Box>
        )}

        <Box className={styles.inputRow} sx={{ opacity: activeRange === "range1" ? 1 : 0.6 }}>
          <div
            className={styles.inputBox}
            onClick={() => {
              setActiveRange("range1")
              setShowCalendar(true)
              setSelectionMode("start")
            }}>
            <Typography className={styles.label}>{isCompare ? "Range 1 From" : "From"}</Typography>
            <div className={styles.inputValue}>{displayDate(startDate)}</div>
          </div>

          <div
            className={styles.inputBox}
            onClick={() => {
              setActiveRange("range1")
              setShowCalendar(true)
              setSelectionMode("end")
            }}>
            <Typography className={styles.label}>{isCompare ? "Range 1 To" : "To"}</Typography>
            <div className={styles.inputValue}>{displayDate(endDate)}</div>
          </div>
        </Box>

        {isCompare && (
          <Box
            className={styles.inputRow}
            sx={{ mt: 1, opacity: activeRange === "range2" ? 1 : 0.6 }}>
            <div
              className={styles.inputBox}
              onClick={() => {
                setActiveRange("range2")
                setShowCalendar(true)
                setSelectionMode("start")
              }}>
              <Typography className={styles.label}>Range 2 From</Typography>
              <div className={styles.inputValue}>{displayDate(startDate2)}</div>
            </div>

            <div
              className={styles.inputBox}
              onClick={() => {
                setActiveRange("range2")
                setShowCalendar(true)
                setSelectionMode("end")
              }}>
              <Typography className={styles.label}>Range 2 To</Typography>
              <div className={styles.inputValue}>{displayDate(endDate2)}</div>
            </div>
          </Box>
        )}
      </Box>

      {showCalendar && (
        <Box className={styles.calendar} sx={{ top: isCompare ? "140px" : "90px" }}>
          <Box className={styles.calendarHeader}>
            <IconButton size="small" onClick={prevMonth}>
              <ArrowBackIosIcon fontSize="small" htmlColor={theme.palette.white.main} />
            </IconButton>

            <Typography className={styles.monthText}>{formatMonthYear(currentMonth)}</Typography>

            <IconButton size="small" onClick={nextMonth}>
              <ArrowForwardIosIcon fontSize="small" htmlColor={theme.palette.white.main} />
            </IconButton>
          </Box>

          <Box
            sx={{
              textAlign: "center",
              mb: 1,
              color: theme.palette.text.secondary,
              fontSize: "12px"
            }}>
            Selecting: {activeRange === "range1" ? "Primary Range" : "Comparison Range"}
          </Box>

          <Box className={styles.weekGrid}>
            {weekDays.map((day, i) => (
              <Typography key={i} className={styles.weekDay}>
                {day}
              </Typography>
            ))}
          </Box>

          <Box className={styles.daysGrid}>
            {generateCalendarDays().map((day, index) => {
              const isSelected = day.date === activeStart || day.date === activeEnd
              const inRange = isInRange(day.date, activeStart, activeEnd)
              const inHoverRange = isInHoverRange(day.date, selectionMode, activeStart, hoverDate)
              const isFuture = isFutureDate(day.date) || isDateDisabled(day.date)

              return (
                <div
                  key={index}
                  style={{
                    ...(isSelected
                      ? { background: theme.palette.secondary.default, color: "#000" }
                      : {}),
                    ...(inRange || inHoverRange
                      ? { background: theme.palette.secondary.default, color: "#000" }
                      : {}),
                    ...(isFuture ? { cursor: "not-allowed", color: "#444" } : {}),
                    ...(!day.isCurrentMonth ? { color: "#444" } : {})
                  }}
                  className={`${styles.day}`}
                  onClick={() => !isFuture && handleDateClick(day.date)}
                  onMouseEnter={() => {
                    if (!isFuture && selectionMode === "end") {
                      setHoverDate(day.date)
                    }
                  }}>
                  {day.day}
                </div>
              )
            })}
          </Box>

          <Box className={styles.footer}>
            <button style={{ color: theme.palette.white.main }} onClick={handleToday}>
              Today
            </button>
            <button
              style={{ color: theme.palette.secondary.default }}
              className={styles.applyBtn}
              onClick={handleApply}>
              Apply
            </button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default AnalyticsDateRangeFilter
